import React, { useMemo } from 'react'
import {
  Box,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableSortLabel,
  Tooltip,
} from '@mui/material'
import InfoIcon from '@mui/icons-material/Info'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { useQuery } from '@tanstack/react-query'
import { auditLogApi, AuditLogItemFull, AuditLogSortField } from '../api/logs'
import { usuariosApi } from '../api/usuarios'
import { sortByLocale } from '../utils/array'
import JsonDetailDialog from '../components/common/JsonDetailDialog'
import ExportButton from '../components/ExportButton'
import ActionBar from '../components/ActionBar'

const compactFieldSx = {
  '& .MuiInputBase-root': {
    borderRadius: 1.5,
    minHeight: 28,
  },
  '& .MuiInputBase-input': {
    py: 0.25,
  },
  '& .MuiInputLabel-root': {
    fontSize: '0.8125rem',
  },
}

const AuditLogPage: React.FC = () => {
  // Nota: Muitos logs podem não ter usuário (user_id/actor_id NULL) porque são:
  // - Logs automáticos de sistema (ex: galatea_web_access, health checks)
  // - Processos background (ex: nfe_process)
  // - Tentativas de login falhadas
  // - Logs legados ou migrados de outros sistemas
  const [createdAtFrom, setCreatedAtFrom] = React.useState<string>('')
  const [createdAtTo, setCreatedAtTo] = React.useState<string>('')
  const [databaseName, setDatabaseName] = React.useState<string>('')
  const [detailsSearch, setDetailsSearch] = React.useState<string>('')
  const [userId, setUserId] = React.useState<number | ''>('')
  const [actorId, setActorId] = React.useState<number | ''>('')
  const [action, setAction] = React.useState<string>('')
  const [sortBy, setSortBy] = React.useState<AuditLogSortField>('id')
  const [sortDirection, setSortDirection] = React.useState<'asc' | 'desc'>('desc')
  const [detailDialog, setDetailDialog] = React.useState<{ open: boolean; content?: string | null }>({
    open: false,
    content: '',
  })

  const { data: usuariosRaw = [] } = useQuery({
    queryKey: ['usuarios', { includeInactive: true }],
    queryFn: () => usuariosApi.list({ includeInactive: true }),
  })
  const usuarios = React.useMemo(
    () => sortByLocale(Array.isArray(usuariosRaw) ? usuariosRaw : [], (u) => u.name ?? ''),
    [usuariosRaw],
  )

  const exportColumns = useMemo(
    () => [
      { key: 'id', label: 'ID' },
      { key: 'created_at', label: 'Data' },
      { key: 'action', label: 'Ação' },
      { key: 'database_name', label: 'Database' },
      { key: 'user_name', label: 'Usuário' },
      { key: 'user_login_thor', label: 'Login Thor Usuário' },
      { key: 'user_email', label: 'Email Usuário' },
      { key: 'actor_name', label: 'Ator' },
      { key: 'actor_login_thor', label: 'Login Thor Ator' },
      { key: 'actor_email', label: 'Email Ator' },
      { key: 'ip', label: 'IP' },
      { key: 'host_name', label: 'Host' },
      { key: 'user_agent', label: 'User Agent' },
      {
        key: 'details',
        label: 'Detalhes',
        format: (value: any) => (value ? JSON.stringify(value) : ''),
      },
    ],
    [],
  )

  // Converter datetime-local para ISO 8601
  const formatDateTimeForAPI = (dateTimeLocal: string): string | null => {
    if (!dateTimeLocal) return null
    // datetime-local retorna formato: YYYY-MM-DDTHH:mm
    // Precisamos converter para ISO 8601 com timezone
    const date = new Date(dateTimeLocal)
    return date.toISOString()
  }

  const { data: logs = [], isLoading, refetch } = useQuery({
    queryKey: [
      'audit-log',
      {
        createdAtFrom: createdAtFrom,
        createdAtTo: createdAtTo,
        databaseName,
        detailsSearch,
        userId,
        actorId,
        action,
        sortBy,
        sortDirection,
      },
    ],
    queryFn: () =>
      auditLogApi.list({
        created_at_from: formatDateTimeForAPI(createdAtFrom),
        created_at_to: formatDateTimeForAPI(createdAtTo),
        database_name: databaseName || null,
        details_search: detailsSearch || null,
        user_id: userId ? Number(userId) : null,
        actor_id: actorId ? Number(actorId) : null,
        action: action || null,
        limit: 200,
        sort_by: sortBy,
        sort_direction: sortDirection,
      }),
  })

  const handleSort = (field: AuditLogSortField) => {
    if (sortBy === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy(field)
      setSortDirection('desc')
    }
  }

  const openDetailDialog = (content?: string | null) => {
    setDetailDialog({ open: true, content })
  }

  return (
    <Box sx={{ py: 4, px: { xs: 2, md: 3 }, width: '100%' }} display="flex" flexDirection="column" gap={0}>
      <ActionBar>
        <ExportButton
          data={logs}
          columns={exportColumns}
          filename="audit_log.xlsx"
          sheetName="Audit Log"
        />
      </ActionBar>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container columnSpacing={1.5} rowSpacing={1.5}>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              margin="dense"
              label="Data Inicial"
              type="datetime-local"
              value={createdAtFrom}
              onChange={(e) => setCreatedAtFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={compactFieldSx}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              margin="dense"
              label="Data Final"
              type="datetime-local"
              value={createdAtTo}
              onChange={(e) => setCreatedAtTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={compactFieldSx}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              margin="dense"
              label="Database Name"
              value={databaseName}
              onChange={(e) => setDatabaseName(e.target.value)}
              sx={compactFieldSx}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              margin="dense"
              label="Busca em Details"
              value={detailsSearch}
              onChange={(e) => setDetailsSearch(e.target.value)}
              sx={compactFieldSx}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small" margin="dense" sx={compactFieldSx}>
              <InputLabel>Usuário</InputLabel>
              <Select value={userId} label="Usuário" onChange={(e) => setUserId(e.target.value as number | '')}>
                <MenuItem value="">Todos</MenuItem>
                {usuarios.map((u: any) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.login_thor ? `${u.login_thor} - ${u.name}` : u.name} {u.email ? `(${u.email})` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small" margin="dense" sx={compactFieldSx}>
              <InputLabel>Ator</InputLabel>
              <Select value={actorId} label="Ator" onChange={(e) => setActorId(e.target.value as number | '')}>
                <MenuItem value="">Todos</MenuItem>
                {usuarios.map((u: any) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.login_thor ? `${u.login_thor} - ${u.name}` : u.name} {u.email ? `(${u.email})` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              margin="dense"
              label="Ação (prefixo)"
              value={action}
              onChange={(e) => setAction(e.target.value)}
              sx={compactFieldSx}
            />
          </Grid>
          <Grid item xs={12} md={3} display="flex" justifyContent="flex-end" alignItems="center">
            <Button
              variant="contained"
              size="small"
              onClick={() => refetch()}
              disabled={isLoading}
              sx={{ minHeight: 28, px: 2 }}
            >
              Aplicar filtros
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 0 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sortDirection={sortBy === 'id' ? sortDirection : false}>
                <TableSortLabel
                  active={sortBy === 'id'}
                  direction={sortBy === 'id' ? sortDirection : 'desc'}
                  onClick={() => handleSort('id')}
                >
                  ID
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortBy === 'created_at' ? sortDirection : false}>
                <TableSortLabel
                  active={sortBy === 'created_at'}
                  direction={sortBy === 'created_at' ? sortDirection : 'desc'}
                  onClick={() => handleSort('created_at')}
                >
                  Data
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortBy === 'database_name' ? sortDirection : false}>
                <TableSortLabel
                  active={sortBy === 'database_name'}
                  direction={sortBy === 'database_name' ? sortDirection : 'desc'}
                  onClick={() => handleSort('database_name')}
                >
                  Database
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortBy === 'action' ? sortDirection : false}>
                <TableSortLabel
                  active={sortBy === 'action'}
                  direction={sortBy === 'action' ? sortDirection : 'desc'}
                  onClick={() => handleSort('action')}
                >
                  Ação
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortBy === 'user_id' ? sortDirection : false}>
                <TableSortLabel
                  active={sortBy === 'user_id'}
                  direction={sortBy === 'user_id' ? sortDirection : 'desc'}
                  onClick={() => handleSort('user_id')}
                >
                  Usuário
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortBy === 'actor_id' ? sortDirection : false}>
                <TableSortLabel
                  active={sortBy === 'actor_id'}
                  direction={sortBy === 'actor_id' ? sortDirection : 'desc'}
                  onClick={() => handleSort('actor_id')}
                >
                  Ator
                </TableSortLabel>
              </TableCell>
              <TableCell>IP</TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <span>Detalhes</span>
                  <Tooltip title="Clique na célula para ver detalhes completos">
                    <InfoIcon fontSize="small" sx={{ color: 'text.secondary', fontSize: '0.9rem' }} />
                  </Tooltip>
                </Box>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {logs.map((l: AuditLogItemFull) => (
              <TableRow key={l.id}>
                <TableCell>{l.id}</TableCell>
                <TableCell>
                  {l.created_at ? new Date(l.created_at).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' }) : '-'}
                </TableCell>
                <TableCell>{l.database_name ?? '-'}</TableCell>
                <TableCell>{l.action}</TableCell>
                <TableCell>
                  {l.user_login_thor && l.user_name
                    ? `${l.user_login_thor} - ${l.user_name}`
                    : l.user_name
                    ? l.user_name
                    : l.user_id
                    ? `ID: ${l.user_id}`
                    : (() => {
                        // Tentar identificar tipo de log do sistema
                        const details = l.details as Record<string, unknown> | null
                        const tipoLog = details?.tipo_log_sistema as string | undefined
                        
                        if (tipoLog) {
                          const tipos: Record<string, string> = {
                            'health_check': 'Health Check',
                            'monitoramento': 'Monitoramento',
                            'sincronizacao': 'Sincronização',
                            'processo_background': 'Processo Background',
                            'script': 'Script',
                            'nfe_process': 'Processamento NF-e',
                            'login_failed': 'Login Falhado',
                          }
                          return (
                            <span style={{ color: '#666', fontStyle: 'italic' }}>
                              {tipos[tipoLog] || tipoLog}
                            </span>
                          )
                        }
                        
                        // Tentar identificar pela ação
                        if (l.action?.includes('health')) {
                          return <span style={{ color: '#666', fontStyle: 'italic' }}>Health Check</span>
                        }
                        if (l.action?.includes('nfe')) {
                          return <span style={{ color: '#666', fontStyle: 'italic' }}>Processamento NF-e</span>
                        }
                        if (l.action?.includes('login.failed')) {
                          return <span style={{ color: '#666', fontStyle: 'italic' }}>Login Falhado</span>
                        }
                        if (l.action?.includes('galatea_web_access')) {
                          return <span style={{ color: '#666', fontStyle: 'italic' }}>Acesso Sistema</span>
                        }
                        
                        return <span style={{ color: '#999', fontStyle: 'italic' }}>Sistema/Automático</span>
                      })()}
                </TableCell>
                <TableCell>
                  {l.actor_login_thor && l.actor_name
                    ? `${l.actor_login_thor} - ${l.actor_name}`
                    : l.actor_name
                    ? l.actor_name
                    : l.actor_id
                    ? `ID: ${l.actor_id}`
                    : (() => {
                        // Tentar identificar tipo de log do sistema
                        const details = l.details as Record<string, unknown> | null
                        const tipoLog = details?.tipo_log_sistema as string | undefined
                        
                        if (tipoLog) {
                          const tipos: Record<string, string> = {
                            'health_check': 'Health Check',
                            'monitoramento': 'Monitoramento',
                            'sincronizacao': 'Sincronização',
                            'processo_background': 'Processo Background',
                            'script': 'Script',
                            'nfe_process': 'Processamento NF-e',
                            'login_failed': 'Login Falhado',
                          }
                          return (
                            <span style={{ color: '#666', fontStyle: 'italic' }}>
                              {tipos[tipoLog] || tipoLog}
                            </span>
                          )
                        }
                        
                        return <span style={{ color: '#999', fontStyle: 'italic' }}>Sistema/Automático</span>
                      })()}
                </TableCell>
                <TableCell>{l.ip ?? '-'}</TableCell>
                <TableCell>
                  {(() => {
                    // Usar APENAS details, nunca details_text
                    const details = l.details ?? null
                    
                    // Se não há details, mostrar indicador
                    if (!details) {
                      return <span style={{ color: '#999', fontStyle: 'italic' }}>Sem detalhes</span>
                    }
                    
                    // Preparar conteúdo para o dialog
                    let dialogContent = ''
                    let parsedDetails: Record<string, unknown> | null = null
                    
                    // Processar details (deve ser objeto)
                    if (details && typeof details === 'object' && !Array.isArray(details)) {
                      parsedDetails = details as Record<string, unknown>
                      dialogContent = JSON.stringify(parsedDetails, null, 2)
                    } else {
                      // Se details não é objeto válido, tentar converter
                      dialogContent = JSON.stringify(details, null, 2)
                    }
                    
                    // Extrair informações relevantes para mostrar como resumo
                    const resumo: string[] = []
                    
                    if (parsedDetails && typeof parsedDetails === 'object') {
                      // Tipo de log do sistema
                      if (parsedDetails.tipo_log_sistema) {
                        resumo.push(`Tipo: ${String(parsedDetails.tipo_log_sistema)}`)
                      }
                      
                      // Origem
                      if (parsedDetails.origem) {
                        resumo.push(`Origem: ${String(parsedDetails.origem)}`)
                      }
                      
                      // Email (para login falhado)
                      if (parsedDetails.email) {
                        resumo.push(`Email: ${String(parsedDetails.email)}`)
                      }
                      
                      // Login
                      if (parsedDetails.login) {
                        resumo.push(`Login: ${String(parsedDetails.login)}`)
                      }
                      
                      // Empresa
                      if (parsedDetails.empresa_id || parsedDetails.nome_empresa) {
                        resumo.push(`Empresa: ${String(parsedDetails.nome_empresa || `ID ${parsedDetails.empresa_id}`)}`)
                      }
                      
                      // Endpoint (para health checks)
                      if (parsedDetails.endpoint) {
                        resumo.push(`Endpoint: ${String(parsedDetails.endpoint)}`)
                      }
                      
                      // IP (se não estiver na coluna IP)
                      if (parsedDetails.ip && parsedDetails.ip !== l.ip) {
                        resumo.push(`IP: ${String(parsedDetails.ip)}`)
                      }
                      
                      // Success/Error
                      if (parsedDetails.success !== undefined) {
                        resumo.push(`Status: ${parsedDetails.success ? 'Sucesso' : 'Erro'}`)
                      }
                      
                      // Contar campos adicionais não exibidos
                      const camposExibidos = ['tipo_log_sistema', 'origem', 'email', 'login', 'empresa_id', 'nome_empresa', 'endpoint', 'ip', 'success']
                      const camposExtras = Object.keys(parsedDetails).filter(
                        k => !camposExibidos.includes(k)
                      )
                      if (camposExtras.length > 0) {
                        resumo.push(`+${camposExtras.length} campo(s)`)
                      }
                      
                      // Se não encontrou nenhum campo conhecido, mostrar todos os campos principais
                      if (resumo.length === 0 && Object.keys(parsedDetails).length > 0) {
                        const keys = Object.keys(parsedDetails).slice(0, 3)
                        keys.forEach(key => {
                          const value = parsedDetails![key]
                          if (value !== null && value !== undefined) {
                            const valueStr = String(value)
                            resumo.push(`${key}: ${valueStr.length > 30 ? valueStr.substring(0, 30) + '...' : valueStr}`)
                          }
                        })
                        if (Object.keys(parsedDetails).length > 3) {
                          resumo.push(`+${Object.keys(parsedDetails).length - 3} mais`)
                        }
                      }
                      
                      // Se ainda não tem resumo mas tem campos, mostrar contagem
                      if (resumo.length === 0 && Object.keys(parsedDetails).length > 0) {
                        resumo.push(`${Object.keys(parsedDetails).length} campo(s) - clique para ver`)
                      }
                    } else {
                      // Se details não é objeto válido
                      resumo.push('Detalhes disponíveis')
                    }
                    
                    const resumoTexto = resumo.length > 0 ? resumo.join(' • ') : 'Detalhes disponíveis (clique para ver)'
                    
                    return (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          cursor: dialogContent ? 'pointer' : 'default',
                          '&:hover': dialogContent ? {
                            color: 'primary.main',
                            textDecoration: 'underline',
                          } : {},
                        }}
                        onClick={() => {
                          if (dialogContent) {
                            openDetailDialog(dialogContent)
                          }
                        }}
                      >
                        {dialogContent && <VisibilityIcon fontSize="small" sx={{ color: 'primary.main' }} />}
                        <Tooltip title={dialogContent ? "Clique para ver detalhes completos" : "Sem detalhes disponíveis"}>
                          <Box
                            sx={{
                              maxWidth: 400,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {resumoTexto}
                          </Box>
                        </Tooltip>
                      </Box>
                    )
                  })()}
                </TableCell>
              </TableRow>
            ))}
            {(!logs || logs.length === 0) && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  Sem registros
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <JsonDetailDialog
        open={detailDialog.open}
        rawContent={detailDialog.content}
        title="Detalhes do log"
        onClose={() => setDetailDialog({ open: false, content: '' })}
      />
    </Box>
  )
}

export default AuditLogPage
